import { InterceptorList } from './InterceptorList';

/**
 * Like the name implies, this is intended to be used as a base data service. It uses an async pipeline model to
 * allow child services to create an execution plan (using request and response interceptors). This also allows
 * external consumers of child services to modify requests as needed for their needs (for instance, to inject
 * an auth token, or a version header).
 */
export class BaseDataService {
  constructor () {
    const interceptors = new InterceptorList();

    Object.defineProperty(this, 'interceptors', {
      get () {
        return interceptors;
      }
    });
  }

  /**
   * Override this in child classes. This method should actually execute the API call. Request interceptors are
   * called before this, and response interceptors are called afterwards.
   * @protected
   */
  _send () {
    // Child classes must override this function
  }

  /**
   * Creates and executes a async pipeline
   * @param config - argument passed to each step of the pipe.
   * @returns {Promise<any>}
   */
  request (config) {
    // Stores the execution plan.
    const requestChain = [];

    // Request interceptors will be called first.
    this.interceptors.request.forEach((interceptor) => {
      requestChain.push(interceptor.onSuccess, interceptor.onError);
    });

    // Then the method that actually executes the $http request. The error handler is undefined because response
    // interceptors should handle errors.
    requestChain.push(this._send.bind(this), undefined);

    // Finally all response interceptors will execute.
    this.interceptors.response.forEach((interceptor) => {
      requestChain.push(interceptor.onSuccess, interceptor.onError);
    });

    // Set up the pipeline as async...
    let promise = Promise.resolve(config);

    // ...and execute it.
    while (requestChain.length) {
      promise = promise.then(requestChain.shift(), requestChain.shift());
    }

    return promise;
  }
}
